import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import type {
  CreateGeoCarvingSessionRequest,
  GeoCarvingManualOverride,
  GeoCarvingSession,
  GeoColumnsResponse,
  GeoNameMappingEntry,
  GetGeoJsonRequest,
  GetGeoJsonResponse,
  RunGeoCarvingRequest,
  SaveGeoColumnPreferencesRequest,
  SaveManualOverridesRequest,
  ScheduledTaskResponse,
} from 'components/SPM/GeoCarving/types';
import { usePlanPrefetchBootstrap } from 'contexts/PlanPrefetchBootstrapContext';
import { useTQPrefetchTokenRegistry } from 'contexts/TQPrefetchTokenContext';
import { deleteRequest, getRequest, patchRequest, postRequest } from 'services/apiRequests';
import type { StringifiedUUID } from 'types';
import type { APIError } from 'utils/errors';
import { CSRFService } from 'services/csrf';
import { QueryHookOptions } from 'types/ReactQuery';

const getApiUrl = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  `/api/v1/tq/hierarchy-models/${hierarchyModelId}/objects/${objectId}/geo-carving/`;

const geoCarvingKeys = {
  base: (hierarchyModelId: string) => ['hierarchy-models', hierarchyModelId, 'geo-carving'] as const,
  session: (hierarchyModelId: string, objectId: string) =>
    [...geoCarvingKeys.base(hierarchyModelId), objectId, 'session'] as const,
  geoNameMapping: (hierarchyModelId: string, objectId: string) =>
    [...geoCarvingKeys.base(hierarchyModelId), objectId, 'geo-name-mapping'] as const,
  columns: (hierarchyModelId: string, objectId: string) =>
    [...geoCarvingKeys.base(hierarchyModelId), objectId, 'columns'] as const,
  geoJson: (hierarchyModelId: string, objectId: string) =>
    [...geoCarvingKeys.base(hierarchyModelId), objectId, 'geoJson'] as const,
};

const getGeoCarvingSession = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  getRequest<null, GeoCarvingSession>(getApiUrl(hierarchyModelId, objectId));

const createGeoCarvingSession = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  params: CreateGeoCarvingSessionRequest,
  csrfToken: string,
) =>
  postRequest<CreateGeoCarvingSessionRequest, ScheduledTaskResponse>(
    getApiUrl(hierarchyModelId, objectId),
    params,
    csrfToken,
  );

type UseGetGeoCarvingSessionQueryOptions = Omit<UseQueryOptions<GeoCarvingSession, APIError>, 'queryKey' | 'queryFn'>;

const useGetGeoCarvingSessionQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseGetGeoCarvingSessionQueryOptions = {},
) => {
  const queryKey = geoCarvingKeys.session(hierarchyModelId as string, objectId as string);
  const queryClient = useQueryClient();
  const query = useQuery<GeoCarvingSession, APIError>({
    enabled: !!hierarchyModelId && !!objectId && options.enabled !== false,
    ...options,
    queryKey,
    queryFn: () => getGeoCarvingSession(hierarchyModelId as string, objectId as string),
  });
  const invalidateQuery = () =>
    queryClient.invalidateQueries({
      queryKey: [...geoCarvingKeys.base(hierarchyModelId as string), objectId as string],
    });
  return { query, invalidateQuery };
};

const useCreateGeoCarvingSessionMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<ScheduledTaskResponse, APIError, CreateGeoCarvingSessionRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<ScheduledTaskResponse, APIError, CreateGeoCarvingSessionRequest>({
    ...options,
    mutationFn: (params: CreateGeoCarvingSessionRequest) =>
      createGeoCarvingSession(hierarchyModelId as string, objectId as string, params, csrfToken),
  });
};

const getGeoNameMapping = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  getRequest<null, GeoNameMappingEntry[]>(`${getApiUrl(hierarchyModelId, objectId)}geo-name-mapping/`);

type UseGetGeoNameMappingQueryOptions = Omit<UseQueryOptions<GeoNameMappingEntry[], APIError>, 'queryKey' | 'queryFn'>;

const useGetGeoNameMappingQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseGetGeoNameMappingQueryOptions = { enabled: true },
) => {
  const queryKey = geoCarvingKeys.geoNameMapping(hierarchyModelId as string, objectId as string);
  const queryClient = useQueryClient();
  const query = useQuery<GeoNameMappingEntry[], APIError>({
    enabled: !!hierarchyModelId && !!objectId && options.enabled,
    ...options,
    queryKey,
    queryFn: () => getGeoNameMapping(hierarchyModelId as string, objectId as string),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey });
  return { query, invalidateQuery };
};

const runGeoCarve = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  params: RunGeoCarvingRequest,
  csrfToken: string,
) =>
  postRequest<RunGeoCarvingRequest, ScheduledTaskResponse>(
    `${getApiUrl(hierarchyModelId, objectId)}carve/`,
    params,
    csrfToken,
  );

const useRunGeoCarveMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<ScheduledTaskResponse, APIError, RunGeoCarvingRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  const queryClient = useQueryClient();

  return useMutation<ScheduledTaskResponse, APIError, RunGeoCarvingRequest>({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...geoCarvingKeys.base(hierarchyModelId as string), objectId as string],
      });
      options.onSuccess?.(data, variables, context);
    },
    mutationFn: (params: RunGeoCarvingRequest) =>
      runGeoCarve(hierarchyModelId as string, objectId as string, params, csrfToken),
  });
};

const dismissWarning = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  warningId: StringifiedUUID,
  csrfToken: string,
) => deleteRequest<null, void>(`${getApiUrl(hierarchyModelId, objectId)}warnings/${warningId}/`, null, csrfToken);

const useDismissWarningMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<void, APIError, StringifiedUUID> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';
  const queryClient = useQueryClient();

  return useMutation<void, APIError, StringifiedUUID>({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...geoCarvingKeys.base(hierarchyModelId as string), objectId as string],
      });
      options.onSuccess?.(data, variables, context);
    },
    mutationFn: (warningId) => dismissWarning(hierarchyModelId as string, objectId as string, warningId, csrfToken),
  });
};

const saveManualOverrides = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  params: SaveManualOverridesRequest,
  csrfToken: string,
) =>
  postRequest<SaveManualOverridesRequest, void>(
    `${getApiUrl(hierarchyModelId, objectId)}manual-overrides/`,
    params,
    csrfToken,
  );

const getGeoJson = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  params: GetGeoJsonRequest,
  csrfToken: string,
) =>
  postRequest<GetGeoJsonRequest, GetGeoJsonResponse>(
    `${getApiUrl(hierarchyModelId, objectId)}manual-overrides-geojson/`,
    params,
    csrfToken,
  );

const useGetManualOverridesGeoJsonQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  manualOverrides: GeoCarvingManualOverride[],
  options: QueryHookOptions<GetGeoJsonResponse, APIError> = {},
) => {
  const queryKey = [...geoCarvingKeys.geoJson(hierarchyModelId as string, objectId as string), manualOverrides];
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useQuery<GetGeoJsonResponse, APIError>({
    ...options,
    // there might be many changes and responses are heavy (one polygon can be around 500kB),
    // set more aggressive cache expiration (when not set, it uses default 5 minutes)
    staleTime: 60 * 1000,
    queryKey,
    queryFn: () =>
      getGeoJson(hierarchyModelId as string, objectId as string, { manual_overrides: manualOverrides }, csrfToken),
  });
};

const useSaveManualOverridesMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<void, APIError, SaveManualOverridesRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';
  const queryClient = useQueryClient();
  const geoJsonQueryKey = geoCarvingKeys.geoJson(hierarchyModelId as string, objectId as string);

  return useMutation<void, APIError, SaveManualOverridesRequest>({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...geoCarvingKeys.base(hierarchyModelId as string), objectId as string],
      });
      queryClient.invalidateQueries({ queryKey: geoJsonQueryKey });
      options.onSuccess?.(data, variables, context);
    },
    mutationFn: (params) => saveManualOverrides(hierarchyModelId as string, objectId as string, params, csrfToken),
  });
};

const applyCarveResults = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  prefetchToken: string,
  csrfToken: string,
) =>
  postRequest<null, void>(
    `${getApiUrl(hierarchyModelId, objectId)}apply/?prefetch_token=${encodeURIComponent(prefetchToken)}`,
    null,
    csrfToken,
  );

const useApplyCarveResultsMutation = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  options: UseMutationOptions<void, APIError, void> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';
  const { prefetchKeyParams } = usePlanPrefetchBootstrap();
  const { getTokenFromParams } = useTQPrefetchTokenRegistry();
  const queryClient = useQueryClient();

  return useMutation<void, APIError, void>({
    ...options,
    onSuccess: (data, variables, context) => {
      // Invalidate all territory objects
      queryClient.invalidateQueries({ queryKey: ['hierarchy-models', hierarchyModelId, 'territory-objects'] });
      // needed to refresh the tree in territory overview
      queryClient.invalidateQueries({ queryKey: ['tqModelTree', hierarchyModelId] });
      queryClient.invalidateQueries({
        queryKey: [...geoCarvingKeys.base(hierarchyModelId as string), objectId as string],
      });
      options.onSuccess?.(data, variables, context);
    },
    mutationFn: async () => {
      const prefetchToken = getTokenFromParams(prefetchKeyParams.accounts);
      if (!prefetchToken) {
        throw new Error('GeocarvingPage.missing_prefetch_token');
      }
      return applyCarveResults(hierarchyModelId, objectId, prefetchToken, csrfToken);
    },
  });
};

const getGeoColumns = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  getRequest<null, GeoColumnsResponse>(`${getApiUrl(hierarchyModelId, objectId)}columns/`);

const saveGeoColumnPreferences = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  params: SaveGeoColumnPreferencesRequest,
  csrfToken: string,
) =>
  patchRequest<SaveGeoColumnPreferencesRequest, GeoColumnsResponse>(
    `${getApiUrl(hierarchyModelId, objectId)}columns/`,
    params,
    csrfToken,
  );

type UseGetGeoColumnsQueryOptions = Omit<UseQueryOptions<GeoColumnsResponse, APIError>, 'queryKey' | 'queryFn'>;

const useGetGeoColumnsQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseGetGeoColumnsQueryOptions = { enabled: true },
) => {
  const queryKey = geoCarvingKeys.columns(hierarchyModelId as string, objectId as string);
  return useQuery<GeoColumnsResponse, APIError>({
    enabled: !!hierarchyModelId && !!objectId && options.enabled,
    ...options,
    queryKey,
    queryFn: () => getGeoColumns(hierarchyModelId as string, objectId as string),
  });
};

const useSaveGeoColumnPreferencesMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<GeoColumnsResponse, APIError, SaveGeoColumnPreferencesRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';
  const queryClient = useQueryClient();

  return useMutation<GeoColumnsResponse, APIError, SaveGeoColumnPreferencesRequest>({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: geoCarvingKeys.columns(hierarchyModelId as string, objectId as string),
      });
      options.onSuccess?.(data, variables, context);
    },
    mutationFn: (params) => saveGeoColumnPreferences(hierarchyModelId as string, objectId as string, params, csrfToken),
  });
};

export const GeoCarvingService = {
  keys: geoCarvingKeys,
  getApiUrl,
  applyCarveResults,
  useGetGeoCarvingSessionQuery,
  useCreateGeoCarvingSessionMutation,
  useSaveGeoColumnPreferencesMutation,
  useGetGeoNameMappingQuery,
  useRunGeoCarveMutation,
  useApplyCarveResultsMutation,
  useDismissWarningMutation,
  useSaveManualOverridesMutation,
  useGetManualOverridesGeoJsonQuery,
  useGetGeoColumnsQuery,
};
